import { createFileRoute } from "@tanstack/react-router";
import { CalendarOff, History, Loader2, Plus, Save, ShieldCheck, Undo2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useAdicionarFeriado,
  useAlterarParametros,
  useFeriados,
  useParametros,
  useRemoverFeriado,
  type FatorRisco,
  type Parametros,
} from "@/api/parametros";
import { useContagensSolicitacoes, useEfetivarAguardando } from "@/api/solicitacoes";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { can, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parametrizacao")({
  component: ParametrizacaoPage,
});

/** As oito listas editáveis do RF-55. */
const LISTAS: { chave: keyof Parametros; rotulo: string; ajuda?: string }[] = [
  { chave: "slotTipos", rotulo: "Tipos de slot" },
  { chave: "bloqueioTipos", rotulo: "Tipos de bloqueio (time interno)" },
  { chave: "frentes", rotulo: "Frentes" },
  { chave: "tiposProjeto", rotulo: "Tipos de entrega" },
  { chave: "papeisEquipe", rotulo: "Papéis da equipe" },
  { chave: "funcoes", rotulo: "Funções dos profissionais" },
  { chave: "motivosReprogramacao", rotulo: "Motivos de reprogramação" },
  { chave: "anexoFormatos", rotulo: "Formatos de anexo aceitos" },
  {
    chave: "ordemCompressao",
    rotulo: "Ordem de compressão do cronograma",
    ajuda:
      "O motor encurta nesta ordem até caber no prazo. A sequência de etapas nunca é quebrada.",
  },
];

const NUMERICOS: {
  grupo: string;
  campos: { chave: keyof Parametros; rotulo: string; ajuda?: string }[];
}[] = [
  {
    grupo: "Grade e capacidade",
    campos: [
      { chave: "horizonteSemanas", rotulo: "Horizonte padrão da grade (semanas)" },
      { chave: "janelaBloqueioDias", rotulo: "Antecedência mínima de bloqueio (dias)" },
      { chave: "slidesPorDia", rotulo: "Slides por dia" },
      { chave: "horasPorDia", rotulo: "Horas por dia" },
      { chave: "slidesIVPorDia", rotulo: "IVs por dia" },
      {
        chave: "tetoProjetosDia",
        rotulo: "Teto de projetos por dia, por profissional",
        ajuda: "Conta OS distintas, não slots. Sinaliza, nunca bloqueia.",
      },
      { chave: "tetoRoteirista", rotulo: "Teto de projetos paralelos por roteirista" },
    ],
  },
  {
    grupo: "Motor de cronograma (PRD §4.1)",
    campos: [
      { chave: "cenarioIdealDias", rotulo: "Cenário Ideal (dias até a entrega)" },
      { chave: "cenarioMedioDias", rotulo: "Cenário Médio (dias)" },
      { chave: "cenarioApertadoDias", rotulo: "Cenário Muito apertado (dias)" },
      { chave: "bufferIVDias", rotulo: "Buffer após a IV (dias úteis)" },
      { chave: "bufferV1Dias", rotulo: "Buffer após a V1 (dias úteis)" },
      {
        chave: "folgaConteudoV1Dias",
        rotulo: "Conteúdo fecha N dias úteis antes da V1",
        ajuda:
          "Sem roteiro aprovado não se produz a V1. O Conteúdo nunca invade a produção: termina com esta distância.",
      },
      {
        chave: "ivAntesDaV1Dias",
        rotulo: "IV fecha N dias úteis antes da V1",
        ajuda:
          "A IV roda em paralelo à cauda do Conteúdo, não no começo dele — são slides que não vão para o lixo.",
      },
      {
        chave: "ensaioAntesVFinalDias",
        rotulo: "Ensaio fecha N dias úteis antes da V Final",
        ajuda: "O Ensaio cai entre as rodadas de alteração, encostado na entrega.",
      },
      { chave: "percentualAlteracao", rotulo: "Percentual de alteração sobre a V1 (%)" },
      {
        chave: "percentualSlidesV1",
        rotulo: "Premissa de slides na V1 (%)",
        ajuda: "Quanto do total de slides a V1 precisa entregar.",
      },
    ],
  },
  {
    grupo: "Limiares do Dashboard",
    campos: [
      { chave: "limiarCapacidadeAtencao", rotulo: "Ocupação — atenção (%)" },
      { chave: "limiarCapacidadeCritico", rotulo: "Ocupação — crítico (%)" },
      { chave: "limiarVolumeCoordenador", rotulo: "Volume alto por Coordenador" },
      { chave: "limiarReprogramacao", rotulo: "Meta de reprogramação (%)" },
    ],
  },
  {
    grupo: "Anexos do briefing",
    campos: [
      { chave: "anexoMaxMB", rotulo: "Tamanho máximo por arquivo (MB)" },
      { chave: "anexoCotaOSMB", rotulo: "Cota por OS (MB)" },
      { chave: "anexoRetencaoDias", rotulo: "Retenção (dias)" },
    ],
  },
];

/**
 * M10 — Parametrização com vigência real (RF-55 a RF-58).
 *
 * Toda alteração cria uma **versão** com data de vigência. No MVP a tela
 * gravava o valor novo no estado global na hora, mesmo com data futura, e
 * nenhum consumidor resolvia por data: a vigência era cosmética.
 */
function ParametrizacaoPage() {
  const role = useRole();
  const podeEditar = can(role, "editarParametros");

  const consulta = useParametros();
  const alterar = useAlterarParametros();
  const [rascunho, setRascunho] = useState<Partial<Parametros>>({});
  const [vigenteDe, setVigenteDe] = useState("");

  const valores = consulta.data?.valores;
  const pendentes = Object.keys(rascunho);

  const editar = <K extends keyof Parametros>(chave: K, valor: Parametros[K]) =>
    setRascunho((r) => ({ ...r, [chave]: valor }));

  const valorDe = <K extends keyof Parametros>(chave: K): Parametros[K] | undefined =>
    (rascunho[chave] ?? valores?.[chave]) as Parametros[K] | undefined;

  const salvar = () =>
    alterar.mutate(
      { mudancas: rascunho, vigenteDe: vigenteDe || undefined },
      {
        onSuccess: ({ vigenteDe: quando }) => {
          setRascunho({});
          setVigenteDe("");
          toast.success(
            quando > new Date().toISOString().slice(0, 10)
              ? `Alteração agendada para ${quando}`
              : "Parâmetros atualizados",
            {
              description:
                "Projetos com cronograma já gerado mantêm os valores com que foram planejados.",
            },
          );
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Não foi possível salvar"),
      },
    );

  return (
    <>
      <TopBar
        title="Parametrização"
        subtitle="Listas, capacidades, limiares e o motor de cronograma · toda alteração cria uma versão"
      />

      <div className="flex-1 space-y-4 overflow-auto p-4">
        {consulta.isPending && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando parâmetros…
          </div>
        )}

        {podeEditar && pendentes.length > 0 && (
          <div className="sticky top-0 z-10 flex flex-wrap items-end gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3 backdrop-blur">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground">
                {pendentes.length} alteração(ões) não salva(s)
              </p>
              <p className="text-[11px] text-muted-foreground">{pendentes.join(", ")}</p>
            </div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Vigente a partir de
              <Input
                type="date"
                value={vigenteDe}
                onChange={(e) => setVigenteDe(e.target.value)}
                className="mt-1 h-8 w-40 text-xs"
              />
            </label>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setRascunho({})}
            >
              <Undo2 className="h-3.5 w-3.5" /> Descartar
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={alterar.isPending}
              onClick={salvar}
            >
              <Save className="h-3.5 w-3.5" /> Salvar versão
            </Button>
          </div>
        )}

        {valores && consulta.data && (
          <>
            <PortaoAceite ligado={valores.exigirAceiteAlocacao} podeEditar={podeEditar} />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {LISTAS.map(({ chave, rotulo, ajuda }) => (
                <ListaEditavel
                  key={chave}
                  rotulo={rotulo}
                  ajuda={ajuda}
                  itens={(valorDe(chave) as string[]) ?? []}
                  podeEditar={podeEditar}
                  onChange={(itens) => editar(chave, itens as Parametros[typeof chave])}
                />
              ))}
            </div>

            {NUMERICOS.map(({ grupo, campos }) => (
              <section key={grupo} className="rounded-xl border border-border bg-card p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">{grupo}</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {campos.map(({ chave, rotulo, ajuda }) => (
                    <label key={chave} className="block">
                      <span className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">
                        {rotulo}
                      </span>
                      <Input
                        type="number"
                        min={0}
                        disabled={!podeEditar}
                        value={String(valorDe(chave) ?? "")}
                        onChange={(e) =>
                          editar(chave, (Number(e.target.value) || 0) as Parametros[typeof chave])
                        }
                        className={cn(
                          "h-8 text-xs",
                          rascunho[chave] !== undefined && "border-primary",
                        )}
                      />
                      {ajuda && (
                        <span className="mt-1 block text-[10px] text-muted-foreground">
                          {ajuda}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </section>
            ))}

            <DirecaoEFatores
              valores={valores}
              rascunho={rascunho}
              podeEditar={podeEditar}
              editar={editar}
              valorDe={valorDe}
            />

            <Feriados podeEditar={podeEditar} />

            <Historico versoes={consulta.data.versoes} />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Liga e desliga o aceite do profissional (RF-53). Grava na hora, sem passar
 * pelo rascunho: é uma chave operacional, e quem a vira quer o efeito agora.
 * Continua sendo uma versão — o histórico mostra quem desligou e quando.
 */
function PortaoAceite({ ligado, podeEditar }: { ligado: boolean; podeEditar: boolean }) {
  const alterar = useAlterarParametros();
  const contagens = useContagensSolicitacoes();
  const efetivar = useEfetivarAguardando();
  const aguardando = contagens.data?.aguardandoAceite ?? 0;

  const alternar = (exigir: boolean) =>
    alterar.mutate(
      {
        mudancas: { exigirAceiteAlocacao: exigir },
        descricao: exigir ? "Aceite do profissional religado" : "Aceite do profissional desligado",
      },
      {
        onSuccess: () =>
          toast.success(
            exigir ? "Aceite do profissional ligado" : "Aceite do profissional desligado",
          ),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Não foi possível salvar"),
      },
    );

  const efetivarTodas = () =>
    efetivar.mutate(undefined, {
      onSuccess: ({ efetivadas, falhas }) => {
        if (!falhas.length) toast.success(`${efetivadas} alocação(ões) efetivada(s) na grade`);
        else
          toast.error(`${efetivadas} efetivada(s), ${falhas.length} recusada(s) pela grade`, {
            description: falhas[0].motivo,
            duration: 12_000,
          });
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Não foi possível efetivar"),
    });

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" /> Aceite do profissional
          </h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {ligado
              ? "Ligado: o primeiro vínculo de um profissional num projeto passa por solicitação e só vira slot depois que ele aceita."
              : "Desligado: quem pode alocar grava direto na grade, e a solicitação aprovada pelo Planejamento já vira slot, sem esperar o aceite."}
          </p>
        </div>
        <Switch
          aria-label="Exigir aceite do profissional"
          checked={ligado}
          disabled={!podeEditar || alterar.isPending}
          onCheckedChange={alternar}
        />
      </div>

      {!ligado && aguardando > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 p-2">
          <p className="text-[11px] text-foreground">
            {aguardando} solicitação(ões) aprovada(s) ainda esperando o aceite.
          </p>
          {podeEditar && (
            <Button
              size="sm"
              className="h-7 text-[11px]"
              disabled={efetivar.isPending}
              onClick={efetivarTodas}
            >
              Efetivar todas na grade
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

function DirecaoEFatores({
  valores,
  rascunho,
  podeEditar,
  editar,
  valorDe,
}: {
  valores: Parametros;
  rascunho: Partial<Parametros>;
  podeEditar: boolean;
  editar: <K extends keyof Parametros>(chave: K, valor: Parametros[K]) => void;
  valorDe: <K extends keyof Parametros>(chave: K) => Parametros[K] | undefined;
}) {
  const fatores = (valorDe("fatoresRisco") as FatorRisco[]) ?? [];
  const [novo, setNovo] = useState("");

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">Direção e fatores de risco</h3>
      <p className="mb-3 mt-0.5 text-[11px] text-muted-foreground">
        Os fatores ativos <strong>elevam a régua</strong> dos cenários Ideal e Médio: o projeto
        precisa de mais dias para se qualificar.
      </p>

      <label className="block max-w-sm">
        <span className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">
          Direção do planejamento
        </span>
        <select
          disabled={!podeEditar}
          value={String(valorDe("direcaoPlanejamento") ?? valores.direcaoPlanejamento)}
          onChange={(e) => editar("direcaoPlanejamento", e.target.value)}
          className={cn(
            "h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground",
            rascunho.direcaoPlanejamento !== undefined && "border-primary",
          )}
        >
          <option>Do prazo para trás</option>
          <option>Da data atual para frente</option>
        </select>
      </label>

      <div className="mt-4 space-y-2">
        {fatores.map((f, i) => (
          <div
            key={f.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/30 p-2"
          >
            <Input
              disabled={!podeEditar}
              value={f.nome}
              onChange={(e) =>
                editar(
                  "fatoresRisco",
                  fatores.map((x, j) => (j === i ? { ...x, nome: e.target.value } : x)),
                )
              }
              className="h-7 min-w-56 flex-1 text-[11px]"
            />
            <label className="flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[10px] text-muted-foreground">
              peso
              <input
                type="number"
                min={0}
                max={100}
                disabled={!podeEditar}
                value={f.peso}
                onChange={(e) =>
                  editar(
                    "fatoresRisco",
                    fatores.map((x, j) =>
                      j === i ? { ...x, peso: Number(e.target.value) || 0 } : x,
                    ),
                  )
                }
                className="w-12 bg-transparent text-right text-[11px] text-foreground focus:outline-none"
              />
            </label>
            <Button
              size="sm"
              variant={f.ativo ? "secondary" : "outline"}
              className="h-7 text-[11px]"
              disabled={!podeEditar}
              onClick={() =>
                editar(
                  "fatoresRisco",
                  fatores.map((x, j) => (j === i ? { ...x, ativo: !x.ativo } : x)),
                )
              }
            >
              {f.ativo ? "Ativo" : "Inativo"}
            </Button>
            {podeEditar && (
              <button
                aria-label="Remover fator"
                onClick={() =>
                  editar(
                    "fatoresRisco",
                    fatores.filter((_, j) => j !== i),
                  )
                }
                className="text-muted-foreground hover:text-health-red"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}

        {podeEditar && (
          <div className="flex gap-2">
            <Input
              value={novo}
              onChange={(e) => setNovo(e.target.value)}
              placeholder="Novo fator de risco"
              className="h-8 max-w-sm text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={() => {
                if (!novo.trim()) return;
                editar("fatoresRisco", [
                  ...fatores,
                  { id: `fr_${Date.now().toString(36)}`, nome: novo.trim(), peso: 10, ativo: true },
                ]);
                setNovo("");
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

/** Feriados e dias não úteis — o motor distribui só em dia útil. */
function Feriados({ podeEditar }: { podeEditar: boolean }) {
  const feriados = useFeriados();
  const adicionar = useAdicionarFeriado();
  const remover = useRemoverFeriado();
  const [data, setData] = useState("");
  const [descricao, setDescricao] = useState("");

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <CalendarOff className="h-4 w-4 text-muted-foreground" /> Dias não úteis
      </h3>
      <p className="mb-3 mt-0.5 text-[11px] text-muted-foreground">
        A grade continua aceitando alocação manual nesses dias — quem não distribui neles é o motor
        de cronograma.
      </p>

      {podeEditar && (
        <div className="mb-3 flex flex-wrap gap-2">
          <Input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="h-8 w-40 text-xs"
          />
          <Input
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descrição (ex.: Recesso da SOAP)"
            className="h-8 max-w-sm text-xs"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={() => {
              if (!data || descricao.trim().length < 2) {
                toast.error("Informe data e descrição");
                return;
              }
              adicionar.mutate(
                { data, descricao: descricao.trim() },
                {
                  onSuccess: () => {
                    setData("");
                    setDescricao("");
                    toast.success("Dia não útil adicionado");
                  },
                  onError: (e) =>
                    toast.error(e instanceof Error ? e.message : "Não foi possível adicionar"),
                },
              );
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Adicionar
          </Button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {(feriados.data ?? []).map((f) => (
          <span
            key={f.data}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 text-[10px] text-muted-foreground"
          >
            <span className="tabular-nums text-foreground">{f.data}</span>
            {f.descricao}
            {podeEditar && !f.nacional && (
              <button
                aria-label={`Remover ${f.data}`}
                onClick={() => remover.mutate(f.data)}
                className="hover:text-health-red"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
    </section>
  );
}

/** RF-57 — histórico de versões, com o que cada uma congelou. */
function Historico({
  versoes,
}: {
  versoes: NonNullable<ReturnType<typeof useParametros>["data"]>["versoes"];
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <History className="h-4 w-4 text-muted-foreground" /> Histórico de versões
      </h3>
      <p className="mb-3 mt-0.5 text-[11px] text-muted-foreground">
        Nada é sobrescrito. Um projeto com cronograma gerado continua respondendo pela versão com
        que foi planejado (RF-56).
      </p>

      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="pb-1.5 font-medium">Vigente de</th>
            <th className="pb-1.5 font-medium">Alteração</th>
            <th className="pb-1.5 font-medium">Por</th>
            <th className="pb-1.5 text-right font-medium">Projetos congelados</th>
          </tr>
        </thead>
        <tbody>
          {versoes.map((v) => (
            <tr key={v.id} className="border-b border-border/50 last:border-0">
              <td className="py-1.5 tabular-nums text-foreground">
                {v.vigenteDe}
                {v.futura && (
                  <span className="ml-1.5 rounded-md bg-health-yellow/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-health-yellow">
                    agendada
                  </span>
                )}
              </td>
              <td className="py-1.5 text-muted-foreground">{v.descricao ?? "—"}</td>
              <td className="py-1.5 text-muted-foreground">{v.criadoPor}</td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {v.projetosCongelados}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ListaEditavel({
  rotulo,
  ajuda,
  itens,
  podeEditar,
  onChange,
}: {
  rotulo: string;
  ajuda?: string;
  itens: string[];
  podeEditar: boolean;
  onChange: (itens: string[]) => void;
}) {
  const [novo, setNovo] = useState("");

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{rotulo}</h3>
      {ajuda && <p className="mt-0.5 text-[11px] text-muted-foreground">{ajuda}</p>}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {itens.map((item, i) => (
          <span
            key={`${item}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 text-[11px] text-foreground"
          >
            {item}
            {podeEditar && (
              <button
                aria-label={`Remover ${item}`}
                onClick={() => onChange(itens.filter((_, j) => j !== i))}
                className="text-muted-foreground hover:text-health-red"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {itens.length === 0 && (
          <span className="text-[11px] text-muted-foreground">Lista vazia</span>
        )}
      </div>

      {podeEditar && (
        <div className="mt-2 flex gap-2">
          <Input
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || !novo.trim()) return;
              onChange([...itens, novo.trim()]);
              setNovo("");
            }}
            placeholder="Adicionar e pressionar Enter"
            className="h-8 text-xs"
          />
        </div>
      )}
    </section>
  );
}
